import { useEffect, useState } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { X } from "lucide-react";
import KhutaSadaDialog from "./KhutaSadaDialog";
import KhutaSadaTrackerDialog from "./KhutaSadaTrackerDialog";
import KhutaCountdown from "./KhutaCountdown";

const STORAGE_KEY = "anjal_welcome_seen_v1";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ARABIC_MONTHS = [
  "يناير", "فبراير", "مارس", "أبريل", "مايو", "يونيو",
  "يوليو", "أغسطس", "سبتمبر", "أكتوبر", "نوفمبر", "ديسمبر",
];
const formatArabicDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((n) => parseInt(n, 10));
  return `${d} ${ARABIC_MONTHS[m - 1]} ${y}`;
};

/**
 * Formal welcome popup — appears once per visitor (localStorage flag).
 * Shows registration status (opens-soon / open / closed) and CTA to register or track.
 */
const WelcomeAnnouncement = () => {
  const [open, setOpen] = useState(false);
  const [khutaEmail, setKhutaEmail] = useState(null);
  const [regStatus, setRegStatus] = useState(null);

  useEffect(() => {
    try {
      setKhutaEmail(localStorage.getItem("khuta_sada_email"));
      const seen = localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        const t = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(t);
      }
    } catch (_) {
      // storage unavailable
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/khuta-sada/registration-status`);
        if (!cancelled) setRegStatus(res.data);
      } catch (_) {
        // non-fatal
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const dismiss = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
    setOpen(false);
  };
  const markSeen = () => {
    try { localStorage.setItem(STORAGE_KEY, "1"); } catch (_) {}
    setOpen(false);
  };

  const isOpen = regStatus?.open;
  const beforeOpen = regStatus && regStatus.today < regStatus.opens_at;
  const afterClose = regStatus && regStatus.today > regStatus.closes_at;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : dismiss())}>
      <DialogContent
        className="w-[94vw] max-w-md p-0 overflow-hidden [&>button:last-child]:hidden sm:rounded-2xl rounded-xl border-0 shadow-2xl"
        dir="rtl"
        data-testid="welcome-announcement"
      >
        <DialogTitle className="sr-only">إعلان ترحيبي</DialogTitle>
        <DialogDescription className="sr-only">
          إعلان عن برنامج للخطى صدى والتسجيل فيه
        </DialogDescription>

        {/* Formal petrol-blue panel */}
        <div
          className="relative px-7 pt-9 pb-7 text-center"
          style={{ background: "linear-gradient(170deg, #0B3A47 0%, #164E63 100%)" }}
        >
          {/* Top gold hairline */}
          <div
            aria-hidden
            className="absolute top-0 inset-x-0 h-[2px]"
            style={{ background: "linear-gradient(90deg, transparent, #D4AF37 50%, transparent)" }}
          />

          {/* Close button */}
          <button
            type="button"
            onClick={dismiss}
            aria-label="إغلاق"
            className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center text-white/70 hover:text-white hover:bg-white/10 transition-colors"
            data-testid="welcome-close-btn"
          >
            <X size={18} />
          </button>

          {/* Small eyebrow label */}
          <p
            className="text-[11px] tracking-[0.35em] font-medium mb-4"
            style={{ color: "#D4AF37" }}
          >
            رابطة الخريجات
          </p>

          <h2
            className="text-3xl sm:text-[2rem] font-bold text-white leading-tight"
            style={{ fontFamily: "Cairo", letterSpacing: "-0.5px" }}
          >
            للخطى صدى
          </h2>

          <div
            className="h-px w-14 mx-auto mt-4 mb-5"
            style={{ background: "#D4AF37" }}
          />

          <p
            className="text-[#D5E3E8] text-sm sm:text-[15px] leading-loose px-1"
            style={{ lineHeight: 2 }}
          >
            برنامج تطوعي مخصّص لخريجات مدارس أنجال التربية،
            <br />
            لتقديم ورشة ملهمة لطالباتنا.
          </p>

          {/* Status strip */}
          {regStatus && (
            <div className="mt-6">
              {isOpen && (
                <>
                  <p className="text-[11px] tracking-[0.25em] text-[#D4AF37] mb-3">
                    ينتهي التسجيل خلال
                  </p>
                  <KhutaCountdown targetIso={regStatus.closes_at} />
                </>
              )}
              {beforeOpen && (
                <>
                  <p className="text-[11px] tracking-[0.25em] text-[#D4AF37] mb-3">
                    يُفتح التسجيل خلال
                  </p>
                  <KhutaCountdown targetIso={regStatus.opens_at} />
                  <p className="text-xs text-[#D5E3E8]/80 mt-4" dir="rtl">
                    من {formatArabicDate(regStatus.opens_at)} إلى {formatArabicDate(regStatus.closes_at)}
                  </p>
                </>
              )}
              {afterClose && (
                <div
                  className="rounded-lg px-4 py-3 text-[13px] text-[#D5E3E8]"
                  style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.25)" }}
                >
                  انتهت فترة التسجيل لهذا الموسم.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Actions on light background */}
        <div className="bg-white px-7 py-6 flex flex-col gap-3">
          {khutaEmail ? (
            <KhutaSadaTrackerDialog
              email={khutaEmail}
              onReset={() => setKhutaEmail(null)}
              trigger={
                <Button
                  onClick={markSeen}
                  className="w-full h-12 text-base font-bold rounded-xl"
                  style={{ backgroundColor: "#D4AF37", color: "#0B3A47" }}
                  data-testid="welcome-track-btn"
                >
                  تتبّع طلبي
                </Button>
              }
            />
          ) : (
            <KhutaSadaDialog
              trigger={
                <Button
                  onClick={markSeen}
                  disabled={regStatus && !isOpen}
                  className="w-full h-12 text-base font-bold rounded-xl disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ backgroundColor: "#D4AF37", color: "#0B3A47" }}
                  data-testid="welcome-register-btn"
                >
                  {beforeOpen ? "التسجيل لم يُفتح بعد" : afterClose ? "التسجيل مغلق" : "سجّلي الآن"}
                </Button>
              }
            />
          )}
          <button
            type="button"
            onClick={dismiss}
            className="w-full h-10 text-sm text-[#64748B] hover:text-[#0B3A47] transition-colors"
            data-testid="welcome-later-btn"
          >
            لاحقًا
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default WelcomeAnnouncement;
